import React from "react";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { IMAGES, SOCIALS, LOGO } from "../../data/content";

export const FinalSection = ({ settings }) => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const socials = [
    { label: "Instagram", href: settings?.instagram_url || SOCIALS[0].href },
    { label: "TikTok", href: settings?.tiktok_url || SOCIALS[1].href },
    { label: "X", href: settings?.x_url || SOCIALS[2].href },
  ];
  return (
    <section data-testid="final-section" className="relative">
      <div className="relative h-[85vh] flex flex-col justify-end overflow-hidden">
        <motion.img
          src={IMAGES.airport}
          alt="Walking through the airport"
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover film-warm"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-matte via-matte/65 to-matte/25" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a1206]/50 via-transparent to-[#3a2a12]/30 mix-blend-soft-light" />

        <div className="relative z-10 px-6 md:px-12 lg:px-16 pb-20 max-w-3xl">
          <Reveal>
            <h2 className="heading text-6xl sm:text-7xl lg:text-9xl text-cream mb-8">One Of None.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-4 text-cream/80 leading-relaxed max-w-xl">
              <p>I used to think there was one way to make it. One road. One timeline. One destination.</p>
              <p>But this game taught me something different. Your path is your path.</p>
              <p>And if you keep showing up, keep working, and keep believing… you never know where it can take you.</p>
              <p className="text-cream">I'm still writing my story. And we're just getting started.</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <button data-testid="final-follow-btn" onClick={() => go("journey")} className="btn-primary mt-9">
              Follow The Journey <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </div>

      <footer data-testid="footer" className="bg-matte border-t border-silver/10 px-6 md:px-12 lg:px-16 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="JT" className="h-16 w-auto" />
            <div>
              <h3 className="heading text-4xl text-cream">Jaron Thames</h3>
              <p className="font-accent text-2xl text-stone -rotate-2">One Of None</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {socials.map((s) => (
              <a
                key={s.label}
                data-testid={`social-${s.label.toLowerCase()}`}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="uppercase tracking-[0.2em] text-xs text-cream/60 hover:text-stone transition-colors"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-silver/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-accent text-2xl text-silver/60">
            Stamped by every city. Built by every season.
          </p>
          <div className="flex items-center gap-6">
            <a
              data-testid="footer-terms-link"
              href="https://customer-assets-7cd3h4nn.emergentagent.net/job_one-of-none/artifacts/9yc9mxc4_JaRon_Thames_Website_Terms_and_Policies.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="uppercase tracking-[0.2em] text-xs text-silver/40 hover:text-stone transition-colors"
            >
              Terms &amp; Policies
            </a>
            <a
              data-testid="footer-admin-link"
              href="/admin/login"
              className="uppercase tracking-[0.2em] text-xs text-silver/40 hover:text-stone transition-colors"
            >
              Admin
            </a>
            <p className="text-silver/30 text-xs">© {new Date().getFullYear()} Jaron Thames. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </section>
  );
};
